// SmartEditUndoCenter — Feb 25, 2026 (Randy pt89c-xxv).
// Persistent home for Smart Edit undo. The post-apply success card was the
// ONLY place undo lived, and it blocked review + vanished on close. This chip
// sits by the Smart create button, shows a badge when there are undoable
// edits, and opens a non-blocking list where each edit keeps a live Undo
// button for the full 24h window — so you can review the calendar first and
// still come back to undo.
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowClockwise, X, ClockCounterClockwise, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";

function fmtLeft(secs) {
  if (secs <= 0) return "expiring";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m left to undo`;
  if (m > 0) return `${m}m left to undo`;
  return `${secs}s left to undo`;
}
function fmtAgo(iso) {
  if (!iso) return "";
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  if (h > 0) return `applied ${h}h ${m}m ago`;
  if (m > 0) return `applied ${m}m ago`;
  return "applied just now";
}

export default function SmartEditUndoCenter({ siloId }) {
  const [edits, setEdits] = useState([]);
  const [open, setOpen] = useState(false);
  const [undoingId, setUndoingId] = useState(null);
  const [err, setErr] = useState("");
  const tickRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/calendar/${siloId}/smart-edit/recent`);
      setEdits(r.data?.edits || []);
    } catch { /* silent — chip just won't show */ }
  }, [siloId]);

  useEffect(() => { load(); }, [load]);

  // Refresh whenever an edit is applied/undone anywhere in the app.
  useEffect(() => {
    const h = () => load();
    window.addEventListener("mysilo:smart-edit-applied", h);
    return () => window.removeEventListener("mysilo:smart-edit-applied", h);
  }, [load]);

  // Live 1s countdown so "Nh Mm left" stays accurate without re-polling.
  useEffect(() => {
    if (!edits.length) return undefined;
    tickRef.current = setInterval(() => {
      setEdits((prev) => prev
        .map((e) => ({ ...e, seconds_left: (e.seconds_left || 0) - 1 }))
        .filter((e) => e.seconds_left > 0));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [edits.length]);

  const doUndo = async (jobId) => {
    setUndoingId(jobId); setErr("");
    try {
      await api.post(`/calendar/${siloId}/smart-edit/${jobId}/undo`);
      setEdits((prev) => prev.filter((e) => e.job_id !== jobId));
      window.dispatchEvent(new Event("mysilo:calendar-refresh"));
    } catch (e) {
      setErr(e.response?.data?.detail || "Undo failed — the window may have just closed.");
      load();
    } finally { setUndoingId(null); }
  };

  if (!edits.length) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="smart-edit-undo-center-chip"
        title="Review or undo recent AI edits"
        className="relative text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border border-[#8A6AC1]/50 text-[#6A4AA1] bg-[#F5F0FC] hover:bg-[#EDE4FA] flex items-center gap-1"
      >
        <ArrowClockwise size={12} weight="bold" /> Undo Recent
        <span
          data-testid="smart-edit-undo-center-count"
          className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#8A6AC1] text-white text-[9px] font-bold"
        >
          {edits.length}
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[250] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-[#FDFCF8] rounded-2xl w-full max-w-md max-h-[85dvh] flex flex-col shadow-2xl border hairline"
            onClick={(e) => e.stopPropagation()}
            data-testid="smart-edit-undo-center-modal"
          >
            <div className="px-5 py-3 border-b hairline flex items-center justify-between shrink-0">
              <h2 className="font-serif-display text-lg flex items-center gap-2">
                <ClockCounterClockwise size={16} weight="duotone" className="text-[#8A6AC1]" /> Undo Recent
              </h2>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="smart-edit-undo-center-close">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-3 flex-1 overflow-y-auto text-sm">
              <p className="text-[11px] text-[#6B6A68] italic">
                Applied a Smart Edit you didn&apos;t want? Undo it here anytime within the window — reviewing the calendar first won&apos;t cost you the chance.
              </p>
              {err && (
                <div className="border border-[#C16E5A] bg-[#FBEBE4] text-[#8A3A28] rounded-md p-2 text-xs">
                  <Warning size={12} weight="duotone" className="inline mr-1" />{err}
                </div>
              )}
              {edits.map((e) => (
                <div key={e.job_id} className="rounded-md border hairline p-3 bg-white" data-testid={`smart-edit-undo-center-row-${e.job_id}`}>
                  <div className="text-[13px] text-[#1C1B1A] leading-snug mb-1">{e.summary}</div>
                  <div className="text-[10px] text-[#6B6A68] flex flex-wrap gap-x-2 gap-y-0.5 mb-2">
                    <span>{e.deleted} deleted</span><span>·</span>
                    <span>{e.created} created</span><span>·</span>
                    <span>{e.modified} modified</span><span>·</span>
                    <span>{e.marked_done} done</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-[#8A6A24]">{fmtAgo(e.applied_at)} · {fmtLeft(e.seconds_left)}</span>
                    <button
                      onClick={() => doUndo(e.job_id)}
                      disabled={undoingId === e.job_id}
                      data-testid={`smart-edit-undo-center-undo-${e.job_id}`}
                      className="text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-md bg-[#1C1B1A] text-white hover:bg-black flex items-center gap-1 disabled:opacity-40 shrink-0"
                    >
                      <ArrowClockwise size={11} weight="bold" /> {undoingId === e.job_id ? "Undoing…" : "Undo"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
